import React, { Fragment } from 'react'
import Configurator from '../../../configurator'

import { redirectWithEvents } from '../../../events-webview'
import { setSystemRedirectLinks } from '../../../admin/actions/system-redirect'
import Link from './sn-link'

/**
 * @description Base button definition
 *      USAGE
 *          <Button
 *              *** PROPS ***
 *          >
 *             Here we go
 *          </Button>
 *
 *      PROPS - all props are optional
 *          className   - button class name definition
 *          href        - if is defined, button will be link
 *          onClick     - onClick handler - if is link, try send webview event first
 *          target      - link target - if is defined, link will be automatically outside link
 *          forceLink   - force outside link
 *          submit      - if is defined, button will be submit button
 *          type        - button type
 *          restProps   - all other props available as React says
 *
 */
export default class Button extends Configurator {

    /**
     * Set system redirect URLs in store
     */
    handleSystemRedirect = () => {
        const { href } = this.props

        setSystemRedirectLinks(null, href, false)
    }

    /**
     * Return true, if link is to Sportnect.com
     * @param {string} link
     * @returns {boolean}
     */
    isOutLinkToSportnect = link => link.includes('sportnect.com') || link.includes('sportnect.local')

    /**
     * Return true, if link is mail or telephone link
     * @param {string} link
     * @returns {boolean}
     */
    isMailOrTelLink = link => link.includes('mailto:') || link.includes('tel:')

    /**
     * Render submit button
     *
     * @return {*}
     */
    renderSubmit = () => {
        const {
            children,
            className,
            submit,
            ...restProps
        } = this.props

        return (
            <input
                type={'submit' || submit}
                value={children}
                className={className}
                disabled={className && className.includes('btn-disabled')}
                {...restProps}
            />
        )
    }

    /**
     * Render internal link button
     *
     * @return {*}
     */
    renderInLink = () => {
        const {
            href,
            onClick,
            children,
            ...restProps
        } = this.props

        return (
            <Link
                to={href}
                onClick={params => redirectWithEvents(window.location.origin + href, null, false) === false && onClick ? onClick(params) : {}}
                {...restProps}
            >
                {children}
            </Link>
        )
    }

    /**
     * Render external link button
     *
     * @return {*}
     */
    renderOutLink = () => {
        const {
            target,
            children,
            href,
            forceLink,
            ...restProps
        } = this.props

        let rel = null
        if (target === '_blank') {
            rel = 'noopener noreferrer'
        }

        const isLinkToTournaments = href.includes('tournaments.sportnect.com')
        const isOutLink = this.isOutLinkToSportnect(href) && !this.isMailOrTelLink(href) && !isLinkToTournaments

        return (
            <Fragment>
                {!isOutLink && (
                    <a
                        target={target || '_self'}
                        rel={rel}
                        href={href}
                        {...restProps}
                    >
                        {children}
                    </a>
                )}
                {isOutLink && (
                    <div
                        className="w-100"
                        onClick={() => this.handleSystemRedirect()}
                    >
                        <a
                            href="#"
                            {...restProps}
                        >
                            {children}
                        </a>
                    </div>
                )}
            </Fragment>
        )
    }

    /**
     * Render
     *
     * @return {*}
     */
    render = () => {
        const {
            children,
            href,
            forceLink,
            submit,
            target,
            type,
            className,
            ...restProps
        } = this.props

        const isLoadingButton = (className || '').includes('btn-loading')
        const isDisabledButton = className && className.includes('btn-disabled')

        if (href) {
            if (forceLink || target) {
                return this.renderOutLink()
            }

            return this.renderInLink()
        }

        if (submit && !isLoadingButton) {
            return this.renderSubmit()
        }

        return (
            <button
                type={type || 'button'}
                className={className}
                disabled={isDisabledButton}
                {...restProps}
            >
                {children}
            </button>
        )
    }
}
